import { Quiz, QuizCompletion } from "@garther/interfaces";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { quizApiClient } from "@/libs/api";

export interface QuizAnswer {
  id: string;
  answer: any;
  timestamp: Date;
}

interface QuizState {
  currentQuiz: QuizCompletion | null;
  questions: Quiz[];
  isLoading: boolean;
  error: string | null;

  // Actions
  setCurrentQuiz: (quiz: QuizCompletion | null) => void;
  setQuestions: (questions: Quiz[]) => void;
  updateAnswer: (questionId: string, answer: any) => void;
  completeQuiz: (score: number) => void;
  resetQuiz: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Async actions
  fetchQuestions: () => Promise<void>;
  fetchQuizData: (userId: string) => Promise<void>;
  saveQuizData: () => Promise<void>;
}

export const useQuizStore = create<QuizState>()(
  devtools(
    (set, get) => ({
      currentQuiz: null,
      questions: [],
      isLoading: false,
      error: null,

      setCurrentQuiz: (quiz: QuizCompletion | null) => {
        set({ currentQuiz: quiz }, false, "setCurrentQuiz");
      },

      setQuestions: (questions: Quiz[]) => {
        set({ questions }, false, "setQuestions");
      },

      updateAnswer: (questionId: string, answer: any) => {
        const { currentQuiz } = get();
        if (!currentQuiz) return;

        console.log("updateAnswer called:", questionId, answer);

        set(
          {
            currentQuiz: {
              ...currentQuiz,
              answers: {
                ...currentQuiz.answers,
                [questionId]: answer
              },
              lastUpdated: new Date()
            }
          },
          false,
          "updateAnswer"
        );
      },

      completeQuiz: (score: number) => {
        const { currentQuiz } = get();
        if (!currentQuiz) return;

        set(
          {
            currentQuiz: {
              ...currentQuiz,
              completed: true,
              completedAt: new Date(),
              score,
              lastUpdated: new Date()
            }
          },
          false,
          "completeQuiz"
        );
      },

      resetQuiz: () => {
        set({ currentQuiz: null, questions: [], error: null }, false, "resetQuiz");
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading }, false, "setLoading");
      },

      setError: (error: string | null) => {
        set({ error }, false, "setError");
      },

      fetchQuestions: async () => {
        set({ isLoading: true, error: null }, false, "fetchQuestions/start");
        try {
          const questions = await quizApiClient.getQuizzes();
          set({ questions, isLoading: false }, false, "fetchQuestions/success");
        } catch (error) {
          set(
            { error: error instanceof Error ? error.message : "Failed to fetch questions", isLoading: false },
            false,
            "fetchQuestions/error"
          );
        }
      },

      fetchQuizData: async (_userId: string) => {
        set({ isLoading: true, error: null }, false, "fetchQuizData/start");
        try {
          const quizData = await quizApiClient.getMyQuizData();

          // If we have existing local data, merge it with fetched data
          const { currentQuiz } = get();
          if (currentQuiz && quizData) {
            // Merge answers, keeping local changes
            const mergedAnswers = {
              ...quizData.answers,
              ...currentQuiz.answers
            };

            const mergedQuizData = {
              ...quizData,
              answers: mergedAnswers,
              lastUpdated: new Date()
            };

            set({ currentQuiz: mergedQuizData, isLoading: false }, false, "fetchQuizData/success");
          } else {
            // No existing data, use fetched data as is
            set({ currentQuiz: quizData, isLoading: false }, false, "fetchQuizData/success");
          }
        } catch (error) {
          set(
            { error: error instanceof Error ? error.message : "Failed to fetch quiz data", isLoading: false },
            false,
            "fetchQuizData/error"
          );
        }
      },

      saveQuizData: async () => {
        const { currentQuiz } = get();
        if (!currentQuiz) return;

        set({ isLoading: true, error: null }, false, "saveQuizData/start");
        try {
          if (currentQuiz.completed) {
            // Complete the quiz
            await quizApiClient.completeMyQuiz({
              answers: currentQuiz.answers
            });
          } else {
            // Update quiz data
            await quizApiClient.updateMyQuizData({
              completed: currentQuiz.completed,
              answers: currentQuiz.answers
            });
          }
          set({ isLoading: false }, false, "saveQuizData/success");
        } catch (error) {
          set(
            { error: error instanceof Error ? error.message : "Failed to save quiz data", isLoading: false },
            false,
            "saveQuizData/error"
          );
        }
      }
    }),
    {
      name: "quiz-store" // Only enable in development
    }
  )
);

// Helper function to calculate quiz score based on answers
export const calculateQuizScore = (answers: Record<string, any>): number => {
  let score = 0;
  const totalQuestions = Object.keys(answers).length;

  if (totalQuestions === 0) return 0;

  // Basic scoring: each answered question gets points
  Object.values(answers).forEach((answer) => {
    if (answer && (typeof answer === "string" || Array.isArray(answer) || typeof answer === "number")) {
      if (Array.isArray(answer) && answer.length > 0) {
        score += 15; // Multiple choice questions get more points
      } else if (typeof answer === "string" && answer.trim()) {
        score += 10; // Single choice questions
      } else if (typeof answer === "number") {
        score += 10; // Scale/degree questions
      }
    }
  });

  // Normalize to 100
  const maxPossibleScore = totalQuestions * 15;
  return Math.round((score / maxPossibleScore) * 100);
};

// Helper function to get quiz recommendations based on answers
export const getQuizRecommendations = (answers: Record<string, any>) => {
  const recommendations = {
    datingStyle: "Balanced",
    communicationPreferences: ["Face-to-face", "Text messages"],
    idealMatch: "Someone who shares your values and communication style",
    compatibilityFactors: ["Shared values", "Communication style", "Life goals"],
    tips: [
      "Be open and honest about your expectations",
      "Take time to understand your partner's perspective",
      "Communicate regularly and clearly"
    ]
  };

  // Analyze answers to provide personalized recommendations
  if (answers["1"]) {
    // Ideal first date
    const datePreference = answers["1"];
    if (datePreference === "Coffee or casual drinks") {
      recommendations.datingStyle = "Casual and Low-pressure";
    } else if (datePreference === "Dinner at a nice restaurant") {
      recommendations.datingStyle = "Traditional and Romantic";
    } else if (datePreference === "Outdoor adventure/activity") {
      recommendations.datingStyle = "Adventurous and Active";
    }
  }

  if (answers["2"]) {
    // Communication preference
    const commPreference = answers["2"];
    if (commPreference === "Face-to-face conversations") {
      recommendations.communicationPreferences = ["Face-to-face", "Video calls"];
    } else if (commPreference === "Text messages") {
      recommendations.communicationPreferences = ["Text messages", "Social media"];
    }
  }

  if (answers["5"]) {
    // Love language
    const loveLanguage = answers["5"];
    recommendations.tips.push(`Focus on expressing love through ${loveLanguage.toLowerCase()}`);
  }

  return recommendations;
};
